import React, { useState, useEffect } from 'react';
import { companyAPI, userAPI } from '../../services/api';
import CompanyModal from './CompanyModal';
import '../../styles/admin/CompanyManagement.css';

/**
 * 企業管理セクション
 */
const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  // 企業一覧を取得
  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await companyAPI.getCompanies();
      setCompanies(data);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
      setError('企業一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // 新規作成モーダルを開く
  const handleCreate = () => {
    setEditingCompany(null);
    setModalOpen(true);
  };

  // 編集モーダルを開く
  const handleEdit = (company) => {
    setEditingCompany(company);
    setModalOpen(true);
  };

  // 企業削除
  const handleDelete = async (companyId, companyName) => {
    try {
      // まず所属ユーザーがいるか確認
      const users = await userAPI.getUsers();
      const associatedUsers = users.filter(user => user.company_id === companyId);

      if (associatedUsers.length > 0) {
        alert('ユーザーが登録されている企業は削除できません。先にユーザーを削除してください。');
        return;
      }

      // 所属ユーザーがいない場合のみ確認ダイアログを表示
      if (!window.confirm(`「${companyName}」を削除してもよろしいですか？`)) {
        return;
      }

      await companyAPI.deleteCompany(companyId);
      alert('企業を削除しました');
      fetchCompanies();
    } catch (err) {
      console.error('Failed to delete company:', err);
      const errorMessage = err.response?.data?.detail || 'ユーザーが登録されている企業は削除できません。先にユーザーを削除してください。';
      alert(errorMessage);
    }
  };

  // モーダルを閉じる
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCompany(null);
  };

  // モーダルから保存成功
  const handleSaveSuccess = () => {
    handleCloseModal();
    fetchCompanies();
  };

  return (
    <div className="company-management">
      <div className="section-header">
        <h2>企業管理</h2>
        <button onClick={handleCreate} className="btn-primary">
          + 新規企業を追加
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading" role="status" aria-live="polite">読み込み中...</div>
      ) : (
        <div className="company-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>企業名</th>
                <th>カラー</th>
                <th>作成日時</th>
                <th>更新日時</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    企業が登録されていません
                  </td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.company_id}>
                    <td>{company.company_id}</td>
                    <td>{company.company_name}</td>
                    <td>
                      <div className="color-display">
                        <span
                          className="color-box"
                          style={{ backgroundColor: company.calendar_color }}
                        ></span>
                        <span>{company.calendar_color}</span>
                      </div>
                    </td>
                    <td>{new Date(company.created_at).toLocaleString('ja-JP')}</td>
                    <td>{new Date(company.updated_at).toLocaleString('ja-JP')}</td>
                    <td className="action-buttons">
                      <button
                        onClick={() => handleEdit(company)}
                        className="btn-edit"
                      >
                        編集
                      </button>
                      <button
                        onClick={() => handleDelete(company.company_id, company.company_name)}
                        className="btn-delete"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 企業作成・編集モーダル */}
      {modalOpen && (
        <CompanyModal
          company={editingCompany}
          onClose={handleCloseModal}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
    </div>
  );
};

export default CompanyManagement;
